import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDashboard } from '../../context/DashboardContext';
import { useConfiguration } from '../../context/ConfigurationContext';
import { impactsData } from '../../data/impactsData';
import { getDefaultThreshold } from '../../utils/severityUtils';

const SettingsModal = () => {
  const {
    showSettingsModal,
    setShowSettingsModal,
    currentCity,
    getImpactOrder,
    saveImpactOrder,
    getOrderedImpacts,
    getIndicatorThreshold,
    saveIndicatorThreshold
  } = useDashboard();
  const { isCityAvailable } = useConfiguration();

  const [orderedImpacts, setOrderedImpacts] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [thresholds, setThresholds] = useState({});
  const [selectedImpactForThreshold, setSelectedImpactForThreshold] = useState(null); // Track which impact's overlay is open
  const [tempThresholds, setTempThresholds] = useState({}); // Temporary thresholds for the overlay
  const [originalThresholds, setOriginalThresholds] = useState({}); // Original thresholds when overlay opens
  const [originalOrder, setOriginalOrder] = useState([]); // Original impact order when modal opens
  const [savedThresholds, setSavedThresholds] = useState({}); // Saved thresholds to compare changes

  // Load current order when modal opens
  useEffect(() => {
    if (showSettingsModal && currentCity) {
      const ordered = getOrderedImpacts(currentCity);
      setOrderedImpacts(ordered);
      setOriginalOrder(ordered.map(impact => impact.id)); // Store original order
      
      // Load thresholds for all indicators
      const thresholdMap = {};
      impactsData.forEach(impact => {
        impact.sectors.forEach(sector => {
          sector.indicators.forEach(indicator => {
            thresholdMap[indicator.name] = getIndicatorThreshold(indicator.name);
          });
        });
      });
      setThresholds(thresholdMap);
      setSavedThresholds({ ...thresholdMap }); // Store saved thresholds for comparison
      setSelectedImpactForThreshold(null); // Reset overlay state when modal opens or city changes
    }
  }, [showSettingsModal, currentCity, getOrderedImpacts, getIndicatorThreshold]);

  const handleDragStart = (e, impact) => {
    setDraggedItem(impact);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, impact) => {
    e.preventDefault();
    if (draggedItem && draggedItem.id !== impact.id) {
      e.currentTarget.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, targetImpact) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    if (!draggedItem || draggedItem.id === targetImpact.id) return;

    const newOrder = [...orderedImpacts];
    const draggedIndex = newOrder.findIndex(i => i.id === draggedItem.id);
    const targetIndex = newOrder.findIndex(i => i.id === targetImpact.id);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setOrderedImpacts(newOrder);
  };

  const hasPriorityChanges = () => {
    const currentOrder = orderedImpacts.map(impact => impact.id);
    return JSON.stringify(currentOrder) !== JSON.stringify(originalOrder);
  };

  const hasUnsavedThresholdChanges = () => {
    // Check if any threshold in the main state differs from saved thresholds
    return Object.keys(thresholds).some(indicatorName => {
      const current = thresholds[indicatorName];
      const saved = savedThresholds[indicatorName];
      
      // Deep comparison for arrays and objects
      if (Array.isArray(current) && Array.isArray(saved)) {
        return JSON.stringify(current) !== JSON.stringify(saved);
      }
      if (typeof current === 'object' && typeof saved === 'object' && current !== null && saved !== null) {
        return JSON.stringify(current) !== JSON.stringify(saved);
      }
      
      return current !== saved;
    });
  };

  const handleResetPriorityOrder = () => {
    const defaultOrder = impactsData.map(impact => impact.id);
    const defaultOrderedImpacts = defaultOrder
      .map(id => impactsData.find(impact => impact.id === id))
      .filter(impact => impact !== undefined);
    setOrderedImpacts(defaultOrderedImpacts);
  };

  const handleSave = () => {
    // Save priority order
    const newOrder = orderedImpacts.map(impact => impact.id);
    saveImpactOrder(currentCity, newOrder);
    setOrderedImpacts(getOrderedImpacts(currentCity));
    setOriginalOrder(newOrder); // Update original order after saving
    
    // Save all thresholds
    Object.keys(thresholds).forEach(indicatorName => {
      saveIndicatorThreshold(indicatorName, thresholds[indicatorName]);
    });
    setSavedThresholds({ ...thresholds }); // Update saved thresholds after saving
    
    alert('Priority order and threshold values saved successfully!');
    // Dispatch custom events to notify components of changes
    window.dispatchEvent(new Event('thresholdChanged'));
    window.dispatchEvent(new Event('priorityOrderChanged'));
    // Don't close the modal - only Close button should close it
  };

  const handleGearClick = (e, impact) => {
    e.stopPropagation(); // Prevent drag from triggering
    // Load current thresholds for this impact's indicators into temp state
    const impactThresholds = {};
    const originalImpactThresholds = {};
    impact.sectors.forEach(sector => {
      sector.indicators.forEach(indicator => {
        const currentValue = thresholds[indicator.name] ?? getDefaultThreshold(indicator.name);
        // Deep clone arrays and objects to avoid reference issues
        if (Array.isArray(currentValue)) {
          impactThresholds[indicator.name] = [...currentValue];
          originalImpactThresholds[indicator.name] = [...currentValue];
        } else if (typeof currentValue === 'object' && currentValue !== null) {
          impactThresholds[indicator.name] = { ...currentValue };
          originalImpactThresholds[indicator.name] = { ...currentValue };
        } else {
          impactThresholds[indicator.name] = currentValue;
          originalImpactThresholds[indicator.name] = currentValue;
        }
      });
    });
    setTempThresholds(impactThresholds);
    setOriginalThresholds(originalImpactThresholds); // Store original values for comparison
    setSelectedImpactForThreshold(impact);
  };

  const hasThresholdChanges = () => {
    if (!selectedImpactForThreshold) return false;
    // Check if any threshold has changed from original
    return Object.keys(tempThresholds).some(indicatorName => {
      const temp = tempThresholds[indicatorName];
      const original = originalThresholds[indicatorName];
      
      // Deep comparison for arrays and objects
      if (Array.isArray(temp) && Array.isArray(original)) {
        return JSON.stringify(temp) !== JSON.stringify(original);
      }
      if (typeof temp === 'object' && typeof original === 'object' && temp !== null && original !== null) {
        return JSON.stringify(temp) !== JSON.stringify(original);
      }
      
      return temp !== original;
    });
  };

  const handleCloseThresholdOverlay = (skipConfirmation = false) => {
    if (!skipConfirmation && hasThresholdChanges()) {
      const confirmed = window.confirm(
        'You have unsaved threshold changes. Are you sure you want to close without saving?'
      );
      if (!confirmed) {
        return; // Don't close if user cancels
      }
    }
    setSelectedImpactForThreshold(null);
    setTempThresholds({});
    setOriginalThresholds({});
  };

  const handleResetThresholds = () => {
    if (!selectedImpactForThreshold) return;
    
    // Reset all thresholds for this impact to their indicator-specific defaults
    const resetThresholds = {};
    selectedImpactForThreshold.sectors.forEach(sector => {
      sector.indicators.forEach(indicator => {
        resetThresholds[indicator.name] = getDefaultThreshold(indicator.name);
      });
    });
    
    setTempThresholds(resetThresholds);
  };

  const handleThresholdOverlaySave = () => {
    if (!selectedImpactForThreshold) return;
    
    // Update the main thresholds state with temp thresholds
    setThresholds(prev => ({
      ...prev,
      ...tempThresholds
    }));
    
    // Update saved thresholds
    setSavedThresholds(prev => ({
      ...prev,
      ...tempThresholds
    }));
    
    // Save to localStorage
    Object.keys(tempThresholds).forEach(indicatorName => {
      saveIndicatorThreshold(indicatorName, tempThresholds[indicatorName]);
    });
    
    alert('Threshold values saved successfully!');
    // Dispatch a custom event to notify components of threshold change
    window.dispatchEvent(new Event('thresholdChanged'));
    
    // Update original thresholds to match saved values (so no confirmation appears on close)
    setOriginalThresholds({ ...tempThresholds });
    
    // Close the overlay without confirmation since we just saved
    handleCloseThresholdOverlay(true);
  };

  // Helper function to check if indicator needs multiple thresholds
  const needsMultipleThresholds = (indicatorName) => {
    const multiThresholdIndicators = [
      'Public transport disruptions (rail, metro, bus)',
      'Road/bridge degradation or closures',
      'Traffic signal / equipment failures',
      'Cooling system failure in public buildings',
      'No. of school/college closure days due to heat',
      '% of schools without basic cooling infrastructure',
      'Drop in per capita water availability (lpcd)',
      'Percentage of wards dependent on water tankers',
      'Temperature differential (urban vs. rural)',
      'Surface temperature & low vegetation zones',
      'Homeless and street dwellers exposed'
    ];
    return multiThresholdIndicators.includes(indicatorName);
  };

  // Helper function to check if indicator needs dual thresholds (surface temperature)
  const needsDualThresholds = (indicatorName) => {
    return indicatorName === 'Surface temperature & low vegetation zones';
  };

  // Helper function to get threshold label with unit (operator comes from threshold data)
  const getThresholdLabel = (indicatorName, severityLevel, index = null, operator = null) => {
    const units = {
      'Public transport disruptions (rail, metro, bus)': ' minutes',
      'Road/bridge degradation or closures': ' days',
      'Traffic signal / equipment failures': ' minutes',
      'Cooling system failure in public buildings': ' minutes',
      'No. of school/college closure days due to heat': ' days',
      '% of schools without basic cooling infrastructure': '%',
      'Drop in per capita water availability (lpcd)': '',
      'Percentage of wards dependent on water tankers': '',
      'Temperature differential (urban vs. rural)': ' deg C',
      'Homeless and street dwellers exposed': '%'
    };

    const unit = units[indicatorName] || '';
    const op = operator || '';
    return `${severityLevel}: ${op}${unit}`;
  };

  // Helper function to get dual threshold labels for surface temperature (operator comes from threshold data)
  const getDualThresholdLabel = (severityLevel, metric, operator = null) => {
    const units = { ndvi: '', lst: ' deg C' };
    const op = operator || '';
    return `${severityLevel}: ${metric.toUpperCase()} ${op}${units[metric]}`;
  };

  const handleTempThresholdChange = (indicatorName, value, index = null, key = null, operatorKey = null) => {
    const defaultThreshold = getDefaultThreshold(indicatorName);
    
    if (needsDualThresholds(indicatorName)) {
      // Handle dual thresholds (surface temperature) - format: { highNdvi: [value, operator], ... }
      const currentThreshold = tempThresholds[indicatorName] || defaultThreshold;
      const updatedThreshold = { ...currentThreshold };
      if (key) {
        const currentValue = updatedThreshold[key];
        if (operatorKey === 'operator') {
          // Update operator
          updatedThreshold[key] = [Array.isArray(currentValue) ? currentValue[0] : (typeof currentValue === 'number' ? currentValue : defaultThreshold[key]?.[0] || 0), value];
        } else {
          // Update value
          const currentOp = Array.isArray(currentValue) ? currentValue[1] : (defaultThreshold[key]?.[1] || '>');
          updatedThreshold[key] = [parseFloat(value) || 0, currentOp];
        }
      }
      setTempThresholds(prev => ({
        ...prev,
        [indicatorName]: updatedThreshold
      }));
    } else if (needsMultipleThresholds(indicatorName)) {
      // Handle array thresholds (3 values) - format: [[value, operator], [value, operator], [value, operator]]
      const currentThreshold = tempThresholds[indicatorName] || defaultThreshold;
      const updatedThreshold = Array.isArray(currentThreshold) ? [...currentThreshold] : [...defaultThreshold];
      if (index !== null && index >= 0 && index < updatedThreshold.length) {
        const currentItem = updatedThreshold[index];
        if (operatorKey === 'operator') {
          // Update operator
          updatedThreshold[index] = [Array.isArray(currentItem) ? currentItem[0] : (typeof currentItem === 'number' ? currentItem : defaultThreshold[index]?.[0] || 0), value];
        } else {
          // Update value
          const currentOp = Array.isArray(currentItem) ? currentItem[1] : (Array.isArray(defaultThreshold[index]) ? defaultThreshold[index][1] : '>');
          updatedThreshold[index] = [parseFloat(value) || 0, currentOp];
        }
      }
      setTempThresholds(prev => ({
        ...prev,
        [indicatorName]: updatedThreshold
      }));
    } else {
      // Handle single threshold (legacy) - format: { value, operator }
      const currentThreshold = tempThresholds[indicatorName] || defaultThreshold;
      if (operatorKey === 'operator') {
        const currentValue = typeof currentThreshold === 'object' && currentThreshold !== null ? currentThreshold.value : (typeof currentThreshold === 'number' ? currentThreshold : defaultThreshold.value || 9000);
        setTempThresholds(prev => ({
          ...prev,
          [indicatorName]: { value: currentValue, operator: value }
        }));
      } else {
        const currentOp = typeof currentThreshold === 'object' && currentThreshold !== null ? currentThreshold.operator : (defaultThreshold.operator || '>=');
        setTempThresholds(prev => ({
          ...prev,
          [indicatorName]: { value: parseFloat(value) || defaultThreshold.value || 9000, operator: currentOp }
        }));
      }
    }
  };


  const handleClose = () => {
    const priorityChanged = hasPriorityChanges();
    const thresholdChanged = hasUnsavedThresholdChanges();

    if (priorityChanged || thresholdChanged) {
      let message = 'You have unsaved changes:\n';
      if (priorityChanged) message += '- Impact priority order\n';
      if (thresholdChanged) message += '- Threshold values\n';
      message += '\nAre you sure you want to close without saving?';
      
      const confirmed = window.confirm(message);
      if (!confirmed) {
        return; // Don't close if user cancels
      }
    }
    
    setShowSettingsModal(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!showSettingsModal) {
    return null;
  }

  const isDataAvailable = isCityAvailable(currentCity);

  return (
    <div className="settings-modal" onClick={handleOverlayClick}>
      <div className="settings-modal-box">
        <div className="settings-modal-header">
          <h3>Settings</h3>
          <button className="close-settings-btn" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div id="settings-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!isDataAvailable ? (
            <p style={{ color: '#666' }}>Data not available for this city.</p>
          ) : (
            <div className="priority-section" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <h3 style={{ color: 'var(--primary)', marginBottom: '0.75rem', fontSize: '1.2rem', flexShrink: 0 }}>
                Impact Priority Ranking
              </h3>

              <div className="priority-edit-mode" style={{ flexShrink: 0, marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="save-priority-btn" onClick={handleSave}>
                    Save Order
                  </button>
                  <button 
                    className="cancel-priority-btn" 
                    onClick={handleResetPriorityOrder}
                    style={{ background: '#6c757d', color: 'white' }}
                  >
                    Reset to Default
                  </button>
                  <button className="cancel-priority-btn" onClick={handleClose}>
                    Close
                  </button>
                </div>
              </div>

              <div 
                className="priority-ranking-list draggable"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'visible',
                  paddingRight: '0.5rem',
                  paddingTop: '1.5rem',
                  minHeight: 0
                }}
              >
                {orderedImpacts.map((impact, index) => {
                  const rank = index + 1;
                  const rankClass = rank <= 4 ? `rank-${rank}` : '';
                  
                  // Get rank color
                  const getRankColor = (r) => {
                    if (r === 1) return '#B71C1C';
                    if (r === 2) return '#EF6C00';
                    if (r === 3) return '#F9A825';
                    if (r === 4) return '#2E7D32';
                    return 'var(--primary)';
                  };
                  const rankColor = getRankColor(rank);

                  return (
                    <div key={impact.id}>
                      <div
                        className="priority-impact-item"
                        draggable
                        onDragStart={(e) => handleDragStart(e, impact)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, impact)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, impact)}
                        style={{ cursor: 'move', position: 'relative' }}
                      >
                        <div className={`priority-impact-rank ${rankClass}`}>
                          {rank}
                        </div>
                        <div className="priority-impact-content" style={{ flex: 1 }}>
                          <div className="priority-impact-title" style={{ color: rankColor }}>
                            {impact.title}
                          </div>
                        </div>
                        <div 
                          style={{ 
                            marginLeft: '1rem',
                            cursor: 'pointer',
                            zIndex: 10
                          }}
                          onClick={(e) => handleGearClick(e, impact)}
                        >
                          <span style={{ 
                            fontSize: '1.2rem',
                            color: '#666',
                            display: 'inline-block',
                            transition: 'color 0.2s'
                          }}>
                            ⚙️
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Instructional text below the impact list */}
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                  borderRadius: '6px',
                  border: '2px solid #2196f3',
                  color: '#000000',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  textAlign: 'center',
                  lineHeight: '1.5'
                }}>
                  Click the gear icon (⚙️) to change indicator threshold values for each impact.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Threshold Overlay Modal */}
      {selectedImpactForThreshold && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleCloseThresholdOverlay}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid #e9ecef'
            }}>
              <h3 style={{
                margin: 0,
                color: 'var(--primary)',
                fontSize: '1.3rem'
              }}>
                {selectedImpactForThreshold.title}
              </h3>
              <button 
                onClick={handleCloseThresholdOverlay}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#999',
                  cursor: 'pointer',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f0f0f0';
                  e.target.style.color = 'var(--primary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#999';
                }}
              >
                &times;
              </button>
            </div>

            <div style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              {selectedImpactForThreshold.sectors.flatMap(sector => sector.indicators).map((indicator) => {
                const defaultThreshold = getDefaultThreshold(indicator.name);
                const currentThreshold = tempThresholds[indicator.name] ?? defaultThreshold;
                const isDualThreshold = needsDualThresholds(indicator.name);
                const isMultiThreshold = needsMultipleThresholds(indicator.name) && !isDualThreshold;
                
                return (
                  <div 
                    key={indicator.name} 
                    style={{ 
                      marginBottom: '1rem',
                      padding: '1rem',
                      background: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #dee2e6'
                    }}
                  >
                    <div style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: 500, 
                      color: '#2c3e50',
                      marginBottom: '0.75rem'
                    }}>
                      {indicator.name}
                    </div>
                    
                    {isDualThreshold ? (
                      // Surface temperature - 6 inputs (dual thresholds) with operators - stacked vertically
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        {[
                          { level: 'high', keys: ['highNdvi', 'highLst'] },
                          { level: 'medium', keys: ['mediumNdvi', 'mediumLst'] },
                          { level: 'low', keys: ['lowNdvi', 'lowLst'] }
                        ].map(({ level, keys }) => {
                          const [ndviKey, lstKey] = keys;
                          const ndviItem = currentThreshold[ndviKey] || defaultThreshold[ndviKey] || [0.1, '<'];
                          const lstItem = currentThreshold[lstKey] || defaultThreshold[lstKey] || [45, '>'];
                          const ndviValue = Array.isArray(ndviItem) ? ndviItem[0] : (typeof ndviItem === 'number' ? ndviItem : 0.1);
                          const ndviOp = Array.isArray(ndviItem) ? ndviItem[1] : '<';
                          const lstValue = Array.isArray(lstItem) ? lstItem[0] : (typeof lstItem === 'number' ? lstItem : 45);
                          const lstOp = Array.isArray(lstItem) ? lstItem[1] : '>';
                          
                          return (
                            <div key={level} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                              <label style={{ fontSize: '0.85rem', color: '#495057', fontWeight: 600 }}>{level.charAt(0).toUpperCase() + level.slice(1)} Severity</label>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  <label style={{ fontSize: '0.8rem', color: '#555', fontWeight: 500 }}>
                                    {getDualThresholdLabel(level, 'ndvi', ndviOp)}
                                  </label>
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <select
                                      value={ndviOp}
                                      onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value, null, ndviKey, 'operator')}
                                      style={{
                                        padding: '0.4rem 0.5rem',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem',
                                        minWidth: '70px'
                                      }}
                                    >
                                      <option value=">">{'>'}</option>
                                      <option value="<">{'<'}</option>
                                      <option value=">=">{'>='}</option>
                                      <option value="<=">{'<='}</option>
                                    </select>
                                    <input
                                      type="number"
                                      value={ndviValue}
                                      onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value, null, ndviKey)}
                                      min="0"
                                      max="1"
                                      step="0.01"
                                      style={{
                                        flex: 1,
                                        maxWidth: '200px',
                                        padding: '0.4rem 0.5rem',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem'
                                      }}
                                    />
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  <label style={{ fontSize: '0.8rem', color: '#555', fontWeight: 500 }}>
                                    {getDualThresholdLabel(level, 'lst', lstOp)}
                                  </label>
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <select
                                      value={lstOp}
                                      onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value, null, lstKey, 'operator')}
                                      style={{
                                        padding: '0.4rem 0.5rem',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem',
                                        minWidth: '70px'
                                      }}
                                    >
                                      <option value=">">{'>'}</option>
                                      <option value="<">{'<'}</option>
                                      <option value=">=">{'>='}</option>
                                      <option value="<=">{'<='}</option>
                                    </select>
                                    <input
                                      type="number"
                                      value={lstValue}
                                      onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value, null, lstKey)}
                                      min="0"
                                      step="0.1"
                                      style={{
                                        flex: 1,
                                        maxWidth: '200px',
                                        padding: '0.4rem 0.5rem',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem'
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : isMultiThreshold ? (
                      // Multiple thresholds - 3 inputs (High, Medium, Low) with operators - stacked vertically
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {[0, 1, 2].map((index) => {
                          const severityLevels = ['high', 'medium', 'low'];
                          const severityLevel = severityLevels[index];
                          const currentItem = Array.isArray(currentThreshold) ? currentThreshold[index] : (Array.isArray(defaultThreshold) ? defaultThreshold[index] : [0, '>']);
                          const thresholdValue = Array.isArray(currentItem) ? currentItem[0] : (typeof currentItem === 'number' ? currentItem : (Array.isArray(defaultThreshold[index]) ? defaultThreshold[index][0] : 0));
                          const thresholdOperator = Array.isArray(currentItem) ? currentItem[1] : (Array.isArray(defaultThreshold[index]) ? defaultThreshold[index][1] : '>');
                          
                          return (
                            <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              <label style={{ fontSize: '0.85rem', color: '#555', fontWeight: 600 }}>
                                {getThresholdLabel(indicator.name, severityLevel, index, thresholdOperator)}
                              </label>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <select
                                  value={thresholdOperator}
                                  onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value, index, null, 'operator')}
                                  style={{
                                    padding: '0.4rem 0.5rem',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem',
                                    minWidth: '70px'
                                  }}
                                >
                                  <option value=">">{'>'}</option>
                                  <option value="<">{'<'}</option>
                                  <option value=">=">{'>='}</option>
                                  <option value="<=">{'<='}</option>
                                </select>
                                <input
                                  type="number"
                                  value={thresholdValue}
                                  onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value, index)}
                                  min="0"
                                  step={indicator.name.includes('water') || indicator.name.includes('Drop') ? '0.01' : '1'}
                                  style={{
                                    flex: 1,
                                    maxWidth: '200px',
                                    padding: '0.4rem 0.5rem',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem'
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      // Single threshold (legacy indicators) with operator
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ 
                          fontSize: '0.85rem', 
                          color: '#555',
                          whiteSpace: 'nowrap'
                        }}>
                          Threshold:
                        </label>
                        <select
                          value={typeof currentThreshold === 'object' && currentThreshold !== null ? currentThreshold.operator : (defaultThreshold.operator || '>=')}
                          onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value, null, null, 'operator')}
                          style={{
                            padding: '0.4rem 0.5rem',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            minWidth: '60px'
                          }}
                        >
                          <option value=">">{'>'}</option>
                          <option value="<">{'<'}</option>
                          <option value=">=">{'>='}</option>
                          <option value="<=">{'<='}</option>
                        </select>
                        <input
                          type="number"
                          value={typeof currentThreshold === 'object' && currentThreshold !== null ? currentThreshold.value : (typeof currentThreshold === 'number' ? currentThreshold : (defaultThreshold.value || 9000))}
                          onChange={(e) => handleTempThresholdChange(indicator.name, e.target.value)}
                          min="0"
                          step={indicator.name === 'Groundwater stress levels' ? '0.1' : '100'}
                          style={{
                            width: '100px',
                            padding: '0.4rem 0.5rem',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: '2px solid #e0e0e0'
            }}>
              <button 
                className="save-priority-btn" 
                onClick={handleThresholdOverlaySave}
                style={{ minWidth: '120px' }}
              >
                Save
              </button>
              <button 
                className="cancel-priority-btn" 
                onClick={handleResetThresholds}
                style={{ background: '#6c757d', color: 'white', minWidth: '120px' }}
              >
                Reset to Default
              </button>
              <button 
                className="cancel-priority-btn" 
                onClick={handleCloseThresholdOverlay}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsModal;

